import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import { useFragmentContext } from '@jsmdg/react-fragment-scripts/fragment';
import { ChevronIcon, Link, LinkVariant, PaybackLogoSVG, PureButton } from '@jsmdg/yoshi';
import { type ProductDetailContext } from '../../types/types';
import {
    trackPaybackAdditionalInfoClicked,
    trackPaybackInfoClicked,
} from './paybackPoints.tracking';
import styles from './PaybackPoints.module.scss';

const calculatePaybackPoints = (price: number, selectedSeatCount: number): number => {
    const priceTotal = selectedSeatCount > 1 ? price * selectedSeatCount : price;

    return Math.floor(priceTotal / 2);
};

type PaybackPointsProps = {
    readonly price?: number;
    readonly selectedSeatCount?: number;
};

const PaybackPoints = ({ price = 0, selectedSeatCount = 1 }: PaybackPointsProps): JSX.Element => {
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const { tenantConfig } = useFragmentContext<ProductDetailContext>();
    const paybackPoints = calculatePaybackPoints(price, selectedSeatCount);

    const handlePaybackInfoClick = (): void => {
        setIsInfoOpen(!isInfoOpen);
        trackPaybackInfoClicked(paybackPoints);
    };

    const handleAdditionalInfoClick = (): void => {
        trackPaybackAdditionalInfoClicked(tenantConfig.urls.payback);
    };

    return (
        <div className="theme-text-body-text mt-3x">
            <PureButton
                className={classNames(styles.infos, 'd-flex position-relative align-items-center')}
                onClick={handlePaybackInfoClick}
            >
                <PaybackLogoSVG className="mr-1x" width={24} height={24} />
                <FormattedMessage
                    defaultMessage="Du erhältst {points}"
                    values={{
                        points: (
                            <span
                                data-testid="paybackPoints"
                                className={`theme-text-body-highlight ml-0-5x ${styles.points}`}
                            >
                                {paybackPoints} &deg;P
                            </span>
                        ),
                    }}
                />
                <ChevronIcon
                    className={classNames(styles.icon, 'ml-1x', {
                        [styles.isExpanded]: isInfoOpen,
                    })}
                />
            </PureButton>
            {isInfoOpen && (
                <div className={classNames(styles.text, 'py-1-5x mt-1x mt-sm-1-5x')}>
                    <FormattedMessage
                        defaultMessage="Gib im Warenkorb deine PAYBACK Kundennummer ein und sammle automatisch Punkte für deinen Einkauf. {moreInfo}"
                        values={{
                            moreInfo: (
                                <Link
                                    href={tenantConfig.urls.payback}
                                    onClick={handleAdditionalInfoClick}
                                    variant={LinkVariant.Grey}
                                >
                                    <FormattedMessage defaultMessage="Weitere Informationen" />
                                </Link>
                            ),
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export { PaybackPoints };
